import React, { useState, useEffect, useMemo } from "react";
import {
  Clock,
  RefreshCw,
  Save,
  Check,
  X,
  Database,
  Search,
  Layers,
  Calendar,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Info,
  Plus,
  Trash2,
  Sparkles,
  CheckSquare,
  Square,
  Building2,
  Globe,
  HelpCircle,
} from "lucide-react";
import {
  getPrazos,
  savePrazo,
  createPrazo,
  getTemposEtapa,
  seedInitialData,
  recalcularDoHistorico,
} from "../services/prazosService";
import {
  getFeriados,
  createFeriado,
  deleteFeriado,
  toggleFeriado,
  buscarFeriadosSugeridos,
  salvarFeriadosEmLote,
} from "../services/calendarioService";
import { normalizeText } from "../utils/formatters";
import { formatDataComDiaSemana } from "../utils/prazoEngine";
import { useProductionContext } from "../contexts/ProductionContext";

export default function PrazosConfigTab({ user }) {
  const { reloadFeriados } = useProductionContext() || {};

  const [prazos, setPrazos] = useState([]);
  const [temposEtapa, setTemposEtapa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editPrometido, setEditPrometido] = useState("");
  const [editMedio, setEditMedio] = useState("");

  // Ações de processamento
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [viewMode, setViewMode] = useState("modelos"); // "modelos" | "matriz" | "calendario"

  // --- ESTADOS DO CALENDÁRIO & FERIADOS ---
  const currentYear = new Date().getFullYear();
  const [selectedAno, setSelectedAno] = useState(currentYear);
  const [feriados, setFeriados] = useState([]);
  const [loadingFeriados, setLoadingFeriados] = useState(false);
  const [searchTermFeriado, setSearchTermFeriado] = useState("");

  // Modal de Sugestão Inteligente (BrasilAPI + SP)
  const [isSugestaoModalOpen, setIsSugestaoModalOpen] = useState(false);
  const [sugestoes, setSugestoes] = useState([]);
  const [loadingSugestoes, setLoadingSugestoes] = useState(false);
  const [salvandoSugestoes, setSalvandoSugestoes] = useState(false);

  // Modal de Adição Manual
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [novoFeriado, setNovoFeriado] = useState({
    data: "",
    descricao: "",
    tipo: "personalizado",
  });
  const [salvandoManual, setSalvandoManual] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [listPrazos, listTempos] = await Promise.all([
        getPrazos(),
        getTemposEtapa(),
      ]);
      setPrazos(listPrazos);
      setTemposEtapa(listTempos);
    } catch (e) {
      console.error("Erro ao carregar configurações de prazos:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditPrometido(item.prazo_prometido ?? "");
    setEditMedio(item.prazo_medio ?? "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditPrometido("");
    setEditMedio("");
  };

  const handleSaveEdit = async (id) => {
    const prometidoNum = Number(editPrometido);
    const medioNum = Number(editMedio);

    if (isNaN(prometidoNum) || prometidoNum < 0) {
      alert("Prazo prometido deve ser um número válido (>= 0).");
      return;
    }

    try {
      const updated = await savePrazo(id, {
        prazo_prometido: prometidoNum,
        prazo_medio: isNaN(medioNum) ? 0 : medioNum,
      });
      setPrazos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...updated } : p))
      );
      setEditingId(null);
    } catch (err) {
      alert("Erro ao salvar prazo: " + err.message);
    }
  };

  const handleToggleAtivo = async (item) => {
    try {
      const updated = await savePrazo(item.id, {
        ativo: !item.ativo,
      });
      setPrazos((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, ...updated } : p))
      );
    } catch (err) {
      alert("Erro ao alterar status: " + err.message);
    }
  };

  const handleSeed = async () => {
    if (
      !window.confirm(
        "Deseja popular as coleções com os dados canônicos da análise histórica (24 modelos e matriz de etapas)?\n\nIsso atualizará os prazos médios e sugeridos de forma segura."
      )
    )
      return;

    try {
      setIsProcessing(true);
      setProgressMsg("Iniciando seed inicial...");
      await seedInitialData((current, total, msg) => {
        setProgressMsg(`[${current}/${total}] ${msg}`);
      });
      alert("Seed concluído com sucesso!");
      await loadData();
    } catch (err) {
      alert("Erro ao realizar seed: " + err.message);
    } finally {
      setIsProcessing(false);
      setProgressMsg("");
    }
  };

  const handleRecalcular = async () => {
    if (
      !window.confirm(
        "Deseja recalcular as sugestões de prazos com base no histórico de pedidos concluídos?\n\nOs prazos sugeridos serão atualizados automaticamente."
      )
    )
      return;

    try {
      setIsProcessing(true);
      setProgressMsg("Recalculando métricas a partir dos pedidos concluídos...");
      const res = await recalcularDoHistorico((curr, tot, msg) => {
        setProgressMsg(msg);
      });
      alert(
        `Recálculo finalizado com sucesso!\n\nPedidos analisados: ${res.totalPedidosAnalisados}\nModelos atualizados: ${res.modelosAtualizados}`
      );
      await loadData();
    } catch (err) {
      alert("Erro no recálculo: " + err.message);
    } finally {
      setIsProcessing(false);
      setProgressMsg("");
    }
  };

  // --- FUNÇÕES DO CALENDÁRIO & DIAS NÃO ÚTEIS ---
  const loadFeriadosDoAno = async (ano) => {
    try {
      setLoadingFeriados(true);
      const list = await getFeriados(ano);
      setFeriados(list);
    } catch (err) {
      console.error("Erro ao carregar feriados:", err);
    } finally {
      setLoadingFeriados(false);
    }
  };

  useEffect(() => {
    if (viewMode === "calendario") {
      loadFeriadosDoAno(selectedAno);
    }
  }, [viewMode, selectedAno]);

  const handleAbrirSugestoes = async () => {
    setIsSugestaoModalOpen(true);
    setLoadingSugestoes(true);
    try {
      const lista = await buscarFeriadosSugeridos(selectedAno);
      const cadastradosMap = new Map(feriados.map((f) => [f.data, f]));
      const adaptados = lista.map((s) => {
        const existe = cadastradosMap.get(s.data);
        return {
          ...s,
          selecionado: existe ? existe.ativo : s.selecionado,
          jaCadastrado: Boolean(existe),
        };
      });
      setSugestoes(adaptados);
    } catch (err) {
      alert("Erro ao buscar sugestões de feriados: " + err.message);
    } finally {
      setLoadingSugestoes(false);
    }
  };

  const handleToggleSugestaoItem = (data) => {
    setSugestoes((prev) =>
      prev.map((s) => (s.data === data ? { ...s, selecionado: !s.selecionado } : s))
    );
  };

  const handleSelectAllSugestoes = (val) => {
    setSugestoes((prev) => prev.map((s) => ({ ...s, selecionado: val })));
  };

  const handleConfirmarSugestoes = async () => {
    const selecionados = sugestoes.filter((s) => s.selecionado);
    if (selecionados.length === 0) {
      alert("Selecione pelo menos um feriado para salvar.");
      return;
    }

    try {
      setSalvandoSugestoes(true);
      const res = await salvarFeriadosEmLote(selectedAno, selecionados);
      await loadFeriadosDoAno(selectedAno);
      if (reloadFeriados) reloadFeriados();
      setIsSugestaoModalOpen(false);
      alert(`Calendário de ${selectedAno} atualizado com sucesso! (${res.criados} criados, ${res.atualizados} atualizados)`);
    } catch (err) {
      alert("Erro ao salvar feriados: " + err.message);
    } finally {
      setSalvandoSugestoes(false);
    }
  };

  const handleSalvarManual = async (e) => {
    e?.preventDefault();
    if (!novoFeriado.data || !novoFeriado.descricao.trim()) {
      alert("Preencha a data e a descrição.");
      return;
    }

    try {
      setSalvandoManual(true);
      const anoData = Number(novoFeriado.data.split("-")[0]);
      await createFeriado({
        data: novoFeriado.data,
        descricao: novoFeriado.descricao.trim(),
        tipo: novoFeriado.tipo,
        ano: anoData || selectedAno,
        ativo: true,
      });

      if (anoData === selectedAno) {
        await loadFeriadosDoAno(selectedAno);
      } else {
        setSelectedAno(anoData);
      }
      if (reloadFeriados) reloadFeriados();
      setIsManualModalOpen(false);
      setNovoFeriado({ data: "", descricao: "", tipo: "personalizado" });
    } catch (err) {
      alert("Erro ao criar dia não útil: " + err.message);
    } finally {
      setSalvandoManual(false);
    }
  };

  const handleToggleAtivoFeriado = async (item) => {
    try {
      const novoAtivo = !item.ativo;
      await toggleFeriado(item.id, novoAtivo);
      setFeriados((prev) =>
        prev.map((f) => (f.id === item.id ? { ...f, ativo: novoAtivo } : f))
      );
      if (reloadFeriados) reloadFeriados();
    } catch (err) {
      alert("Erro ao alternar status do feriado: " + err.message);
    }
  };

  const handleDeleteFeriadoItem = async (id, desc) => {
    if (!window.confirm(`Deseja remover "${desc}" do calendário da oficina?`)) return;
    try {
      await deleteFeriado(id);
      setFeriados((prev) => prev.filter((f) => f.id !== id));
      if (reloadFeriados) reloadFeriados();
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    }
  };

  const getTipoBadge = (tipo) => {
    switch (tipo) {
      case "nacional":
        return { label: "Feriado Nacional", bg: "bg-emerald-100 text-emerald-800" };
      case "estadual":
        return { label: "Estadual (SP)", bg: "bg-blue-100 text-blue-800" };
      case "municipal":
        return { label: "Municipal (SP Capital)", bg: "bg-indigo-100 text-indigo-800" };
      case "emenda":
        return { label: "Emenda / Ponte", bg: "bg-amber-100 text-amber-800" };
      case "recesso":
        return { label: "Recesso Coletivo", bg: "bg-purple-100 text-purple-800" };
      case "facultativo":
        return { label: "Ponto Facultativo", bg: "bg-yellow-100 text-yellow-800" };
      default:
        return { label: "Personalizado / Fábrica", bg: "bg-slate-100 text-slate-700" };
    }
  };

  const filteredFeriados = useMemo(() => {
    if (!searchTermFeriado.trim()) return feriados;
    const query = normalizeText(searchTermFeriado);
    return feriados.filter(
      (f) =>
        normalizeText(f.descricao).includes(query) ||
        normalizeText(f.tipo).includes(query) ||
        f.data.includes(query)
    );
  }, [feriados, searchTermFeriado]);

  // Filtragem por busca
  const filteredPrazos = prazos.filter((p) => {
    if (!searchTerm) return true;
    return normalizeText(p.modelo).includes(normalizeText(searchTerm));
  });

  // Agrupamento para visão em matriz
  const etapasDistintas = [
    "SOLICITACAO",
    "MODELAGEM",
    "IMPRIMINDO",
    "CURA",
    "FUNDICAO",
    "BANCA",
    "POLIMENTO",
    "RESINA_FINALIZACAO",
  ];

  const matrizMap = {};
  temposEtapa.forEach((t) => {
    if (!matrizMap[t.modelo]) matrizMap[t.modelo] = {};
    matrizMap[t.modelo][t.etapa] = t.dias_ate_conclusao;
  });

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 bg-slate-50">
      {/* OVERLAY DE PROCESSAMENTO */}
      {isProcessing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex flex-col items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl flex flex-col items-center gap-4 text-center animate-in zoom-in duration-200">
            <RefreshCw size={36} className="text-blue-600 animate-spin" />
            <h3 className="font-bold text-slate-800 text-sm">Processando</h3>
            <p className="text-xs text-slate-500 font-mono">{progressMsg}</p>
          </div>
        </div>
      )}

      {/* HEADER PRINCIPAL */}
      <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
            <Clock size={24} />
          </div>
          <div>
            <h2 className="text-base font-black text-slate-800 uppercase tracking-wider">
              Prazos de Fabricação por Modelo
            </h2>
            <p className="text-xs text-slate-500">
              Configure o prazo prometido ao cliente na venda e visualize a
              referência histórica e sugestões da oficina.
            </p>
          </div>
        </div>

        {/* BOTÕES DE AÇÃO */}
        <div className="flex flex-wrap items-center gap-2">
          {prazos.length === 0 && (
            <button
              onClick={handleSeed}
              disabled={loading || isProcessing}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all"
              title="Carrega os 24 modelos e a matriz da análise inicial"
            >
              <Database size={14} />
              <span>Popular Dados Iniciais (Seed)</span>
            </button>
          )}

          <button
            onClick={handleRecalcular}
            disabled={loading || isProcessing || prazos.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all"
            title="Recalcula as medianas a partir dos pedidos concluídos no banco"
          >
            <RefreshCw size={14} />
            <span>Recalcular Sugestão pelo Histórico</span>
          </button>
        </div>
      </div>

      {/* CARD INFORMATIVO DE CONCEITOS */}
      <div className="bg-blue-50/60 border border-blue-200/80 rounded-xl p-4 flex items-start gap-3 text-xs text-blue-900">
        <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <div className="space-y-1 leading-relaxed">
          <p>
            <strong>Prazo Prometido:</strong> Acordo de venda com o cliente em
            dias úteis. É a régua que define se o pedido atrasou de verdade.
          </p>
          <p>
            <strong>Prazo Médio / Sugerido:</strong> Mediana observada a partir
            do histórico real de produção concluída. Alimenta as previsões
            dinâmicas.
          </p>
        </div>
      </div>

      {/* BARRA DE PESQUISA E SELEÇÃO DE VISÃO */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          {viewMode === "calendario" ? (
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              placeholder="Buscar feriado ou data (ex: Tiradentes, 09/07)..."
              value={searchTermFeriado}
              onChange={(e) => setSearchTermFeriado(e.target.value)}
            />
          ) : (
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              placeholder="Buscar modelo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
        </div>

        <div className="flex items-center gap-1 bg-white border border-slate-200 p-1 rounded-lg">
          <button
            onClick={() => setViewMode("modelos")}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-all ${
              viewMode === "modelos"
                ? "bg-blue-600 text-white shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Tabela de Prazos
          </button>
          <button
            onClick={() => setViewMode("matriz")}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-all ${
              viewMode === "matriz"
                ? "bg-blue-600 text-white shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Matriz Etapas x Modelo
          </button>
          <button
            onClick={() => setViewMode("calendario")}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-all flex items-center gap-1.5 ${
              viewMode === "calendario"
                ? "bg-blue-600 text-white shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Calendar size={13} />
            <span>Calendário & Feriados (SP)</span>
          </button>
        </div>
      </div>

      {/* VISÃO 1: TABELA DE MODELOS E PRAZOS */}
      {viewMode === "modelos" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-xs text-slate-400">
              Carregando prazos de modelos...
            </div>
          ) : filteredPrazos.length === 0 ? (
            <div className="p-8 text-center space-y-3">
              <Clock size={32} className="mx-auto text-slate-300" />
              <p className="text-xs text-slate-500 font-bold">
                Nenhum modelo cadastrado na coleção de prazos.
              </p>
              <button
                onClick={handleSeed}
                className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700"
              >
                Carregar 24 Modelos Iniciais
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold uppercase text-[10px]">
                    <th className="p-3.5">Modelo</th>
                    <th className="p-3.5 text-center">
                      Prazo Prometido (Venda)
                    </th>
                    <th className="p-3.5 text-center">
                      Prazo Médio Histórico
                    </th>
                    <th className="p-3.5 text-center">Sugerido Histórico</th>
                    <th className="p-3.5 text-center">Taxa Retrabalho</th>
                    <th className="p-3.5 text-center">Status</th>
                    <th className="p-3.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPrazos.map((item) => {
                    const isEditing = editingId === item.id;
                    const semPrazo =
                      !item.prazo_prometido || Number(item.prazo_prometido) <= 0;

                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-slate-50/50 transition-colors ${
                          semPrazo ? "bg-amber-50/30" : ""
                        }`}
                      >
                        {/* Modelo */}
                        <td className="p-3.5 font-bold text-slate-800">
                          <div className="flex items-center gap-2">
                            <span>{item.modelo}</span>
                            {semPrazo && (
                              <span
                                className="bg-red-100 text-red-700 text-[9px] font-black px-1.5 py-0.5 rounded uppercase flex items-center gap-1"
                                title="Defina o prazo prometido para este modelo"
                              >
                                <AlertTriangle size={10} /> Não Definido
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Prazo Prometido */}
                        <td className="p-3.5 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="number"
                                min="0"
                                max="60"
                                className="w-16 p-1.5 border border-blue-400 rounded text-center text-xs font-bold text-blue-700 focus:outline-none"
                                value={editPrometido}
                                onChange={(e) =>
                                  setEditPrometido(e.target.value)
                                }
                              />
                              <span className="text-[10px] text-slate-400 font-bold">
                                d.ú.
                              </span>
                            </div>
                          ) : (
                            <span
                              className={`font-mono font-extrabold text-sm ${
                                semPrazo ? "text-red-500" : "text-blue-700"
                              }`}
                            >
                              {item.prazo_prometido ?? "-"} d.ú.
                            </span>
                          )}
                        </td>

                        {/* Prazo Médio */}
                        <td className="p-3.5 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                className="w-16 p-1.5 border border-slate-300 rounded text-center text-xs font-bold text-slate-700 focus:outline-none"
                                value={editMedio}
                                onChange={(e) => setEditMedio(e.target.value)}
                              />
                              <span className="text-[10px] text-slate-400 font-bold">
                                d.ú.
                              </span>
                            </div>
                          ) : (
                            <span className="font-mono text-slate-600 font-bold">
                              {item.prazo_medio ? `${item.prazo_medio} d.ú.` : "-"}
                            </span>
                          )}
                        </td>

                        {/* Prazo Sugerido */}
                        <td className="p-3.5 text-center">
                          <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full text-[10px] font-extrabold font-mono border border-slate-200">
                            {item.prazo_sugerido
                              ? `${item.prazo_sugerido} dias`
                              : "-"}
                          </span>
                        </td>

                        {/* Taxa Retrabalho */}
                        <td className="p-3.5 text-center">
                          <span className="text-[10px] font-mono text-slate-500">
                            {item.fator_risco
                              ? `${Math.round(item.fator_risco * 100)}%`
                              : "0%"}
                          </span>
                        </td>

                        {/* Status Ativo */}
                        <td className="p-3.5 text-center">
                          <button
                            onClick={() => handleToggleAtivo(item)}
                            className="flex items-center justify-center gap-1 mx-auto hover:opacity-80 transition-opacity"
                          >
                            {item.ativo ? (
                              <ToggleRight
                                size={22}
                                className="text-blue-600"
                              />
                            ) : (
                              <ToggleLeft size={22} className="text-slate-300" />
                            )}
                          </button>
                        </td>

                        {/* Ações */}
                        <td className="p-3.5 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleSaveEdit(item.id)}
                                className="p-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-md transition-colors"
                                title="Salvar alterações"
                              >
                                <Check size={15} />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                title="Cancelar"
                              >
                                <X size={15} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleStartEdit(item)}
                              className="px-2.5 py-1 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded font-bold text-[11px] transition-colors"
                            >
                              Editar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* VISÃO 2: MATRIZ DE ETAPAS X MODELO */}
      {viewMode === "matriz" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-4 space-y-3">
          <div>
            <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
              Dias Úteis Restantes até a Conclusão (por Etapa e Modelo)
            </h3>
            <p className="text-[11px] text-slate-500">
              Valor mediano de dias úteis que faltam para o pedido ficar pronto
              ao entrar em cada etapa fabril.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse border border-slate-200">
              <thead>
                <tr className="bg-slate-50 text-slate-600 font-bold uppercase text-[9px] border-b border-slate-200">
                  <th className="p-2.5 border-r border-slate-200 sticky left-0 bg-slate-50">
                    Etapa \ Modelo
                  </th>
                  {filteredPrazos.map((p) => (
                    <th
                      key={p.id}
                      className="p-2.5 text-center border-r border-slate-200 whitespace-nowrap min-w-[70px]"
                    >
                      {p.modelo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                {etapasDistintas.map((etapa) => (
                  <tr key={etapa} className="hover:bg-slate-50/50">
                    <td className="p-2 font-bold text-slate-700 uppercase bg-slate-50 border-r border-slate-200 sticky left-0 text-[10px]">
                      {etapa}
                    </td>
                    {filteredPrazos.map((p) => {
                      const dias = matrizMap[p.modelo]?.[etapa];
                      const valNum = dias !== undefined ? Number(dias) : null;
                      return (
                        <td
                          key={p.id}
                          className={`p-2 text-center border-r border-slate-100 ${
                            valNum !== null && valNum > 4
                              ? "bg-amber-50/60 text-amber-800 font-bold"
                              : "text-slate-600"
                          }`}
                        >
                          {valNum !== null ? `${valNum} d` : "-"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VISÃO 3: CALENDÁRIO & DIAS NÃO ÚTEIS (SP & BRASIL) */}
      {viewMode === "calendario" && (
        <div className="space-y-4">
          {/* BARRA SUPERIOR DO CALENDÁRIO: SELETOR DE ANO + BOTÕES DE AÇÃO */}
          <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Ano de Referência:
              </span>
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
                {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((yr) => (
                  <button
                    key={yr}
                    onClick={() => setSelectedAno(yr)}
                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                      selectedAno === yr
                        ? "bg-blue-600 text-white shadow-xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {yr}
                  </button>
                ))}
              </div>

              <span className="text-xs text-slate-500 font-medium">
                ({filteredFeriados.length} {filteredFeriados.length === 1 ? "dia cadastrado" : "dias cadastrados"})
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleAbrirSugestoes}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all cursor-pointer"
                title="Buscar feriados oficiais na BrasilAPI e enriquecer com feriados de São Paulo Capital"
              >
                <Sparkles size={14} className="text-yellow-300" />
                <span>Sugerir Feriados (BrasilAPI + SP)</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setNovoFeriado({
                    data: `${selectedAno}-`,
                    descricao: "",
                    tipo: "personalizado",
                  });
                  setIsManualModalOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all cursor-pointer"
                title="Cadastrar emenda, recesso ou parada fabril manualmente"
              >
                <Plus size={14} />
                <span>Adicionar Data Manual</span>
              </button>
            </div>
          </div>

          {/* TABELA DE FERIADOS E DIAS NÃO ÚTEIS */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {loadingFeriados ? (
              <div className="p-8 text-center text-xs text-slate-400">
                Carregando calendário de {selectedAno}...
              </div>
            ) : filteredFeriados.length === 0 ? (
              <div className="p-10 text-center space-y-3">
                <Calendar size={36} className="mx-auto text-slate-300" />
                <p className="text-xs text-slate-600 font-bold">
                  Nenhum feriado ou dia não útil cadastrado para {selectedAno}.
                </p>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Clique no botão abaixo para carregar as sugestões oficiais da BrasilAPI e de São Paulo Capital com 1 clique.
                </p>
                <button
                  type="button"
                  onClick={handleAbrirSugestoes}
                  className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 shadow-sm transition-all inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <Sparkles size={14} className="text-yellow-300" />
                  <span>Sugerir Feriados de {selectedAno}</span>
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold uppercase text-[10px]">
                      <th className="p-3.5">Data & Dia da Semana</th>
                      <th className="p-3.5">Descrição</th>
                      <th className="p-3.5">Tipo / Âmbito</th>
                      <th className="p-3.5 text-center">Status no Cálculo</th>
                      <th className="p-3.5 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredFeriados.map((item) => {
                      const tipoBadge = getTipoBadge(item.tipo);
                      return (
                        <tr
                          key={item.id}
                          className={`hover:bg-slate-50/70 transition-colors ${
                            item.ativo === false ? "opacity-45 bg-slate-50/40" : ""
                          }`}
                        >
                          <td className="p-3.5 font-mono font-bold text-slate-800">
                            {formatDataComDiaSemana(item.data)}
                          </td>
                          <td className="p-3.5 font-medium text-slate-700">
                            {item.descricao}
                          </td>
                          <td className="p-3.5">
                            <span
                              className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-bold inline-block ${tipoBadge.bg}`}
                            >
                              {tipoBadge.label}
                            </span>
                          </td>
                          <td className="p-3.5 text-center">
                            <button
                              type="button"
                              onClick={() => handleToggleAtivoFeriado(item)}
                              className={`text-xs font-bold inline-flex items-center justify-center gap-1.5 cursor-pointer transition-all ${
                                item.ativo !== false
                                  ? "text-emerald-700 hover:text-emerald-800"
                                  : "text-slate-400 hover:text-slate-600"
                              }`}
                              title="Clique para ativar/desativar este dia no cálculo de prazos da oficina"
                            >
                              {item.ativo !== false ? (
                                <>
                                  <ToggleRight size={22} className="text-emerald-600" />
                                  <span className="text-[10px] font-bold">Ativo (Fábrica Fecha)</span>
                                </>
                              ) : (
                                <>
                                  <ToggleLeft size={22} className="text-slate-400" />
                                  <span className="text-[10px] font-bold">Inativo (Trabalha)</span>
                                </>
                              )}
                            </button>
                          </td>
                          <td className="p-3.5 text-right">
                            <button
                              type="button"
                              onClick={() => handleDeleteFeriadoItem(item.id, item.descricao)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                              title="Excluir dia do calendário"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 1: SUGESTÃO INTELIGENTE DE FERIADOS (BRASILAPI + SP CAPITAL) */}
      {isSugestaoModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[85vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Sparkles size={18} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">
                    Feriados de {selectedAno} (BrasilAPI & SP)
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Selecione quais dias a oficina estará fechada e confirme.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSugestaoModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content List */}
            <div className="p-4 overflow-y-auto flex-1 space-y-2">
              {loadingSugestoes ? (
                <div className="p-8 text-center text-xs text-slate-400">
                  Consultando BrasilAPI e gerando calendário de São Paulo...
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between pb-2 border-b border-slate-100 text-xs">
                    <span className="text-slate-500 font-bold">
                      {sugestoes.filter((s) => s.selecionado).length} de {sugestoes.length} selecionados
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSelectAllSugestoes(true)}
                        className="text-blue-600 hover:underline font-bold text-[11px] cursor-pointer"
                      >
                        Marcar Todos
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={() => handleSelectAllSugestoes(false)}
                        className="text-slate-500 hover:underline font-bold text-[11px] cursor-pointer"
                      >
                        Desmarcar Todos
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5 pt-1">
                    {sugestoes.map((item) => {
                      const tipoBadge = getTipoBadge(item.tipo);
                      return (
                        <div
                          key={item.data}
                          onClick={() => handleToggleSugestaoItem(item.data)}
                          className={`p-2.5 rounded-lg border flex items-center justify-between gap-3 cursor-pointer transition-all ${
                            item.selecionado
                              ? "bg-blue-50/70 border-blue-300 text-slate-800"
                              : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <input
                              type="checkbox"
                              checked={item.selecionado}
                              onChange={() => {}} // tratado pelo onClick do container
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 pointer-events-none"
                            />
                            <div className="min-w-0">
                              <span className="font-bold text-xs block truncate">
                                {item.descricao}
                              </span>
                              <span className="text-[10px] font-mono opacity-80">
                                {formatDataComDiaSemana(item.data)}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {item.jaCadastrado && (
                              <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-bold">
                                Já no Banco
                              </span>
                            )}
                            <span
                              className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-bold ${tipoBadge.bg}`}
                            >
                              {tipoBadge.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setIsSugestaoModalOpen(false)}
                className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-lg text-xs font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarSugestoes}
                disabled={salvandoSugestoes || loadingSugestoes}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {salvandoSugestoes ? (
                  <span>Salvando no PocketBase...</span>
                ) : (
                  <>
                    <Save size={14} />
                    <span>Salvar Selecionados no Calendário</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: ADIÇÃO MANUAL DE DIA NÃO ÚTIL (EMENDAS, RECESSOS) */}
      {isManualModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <form onSubmit={handleSalvarManual}>
              {/* Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <Plus className="text-emerald-600" size={18} />
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">
                    Adicionar Dia Não Útil
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsManualModalOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Fields */}
              <div className="p-4 space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 uppercase text-[10px] mb-1">
                    Data (Dia/Mês/Ano) *
                  </label>
                  <input
                    type="date"
                    required
                    value={novoFeriado.data}
                    onChange={(e) =>
                      setNovoFeriado({ ...novoFeriado, data: e.target.value })
                    }
                    className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase text-[10px] mb-1">
                    Descrição / Motivo *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Emenda de Corpus Christi, Férias Coletivas..."
                    value={novoFeriado.descricao}
                    onChange={(e) =>
                      setNovoFeriado({ ...novoFeriado, descricao: e.target.value })
                    }
                    className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase text-[10px] mb-1">
                    Tipo de Paralisação
                  </label>
                  <select
                    value={novoFeriado.tipo}
                    onChange={(e) =>
                      setNovoFeriado({ ...novoFeriado, tipo: e.target.value })
                    }
                    className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                  >
                    <option value="emenda">Emenda / Ponte de Feriado</option>
                    <option value="recesso">Recesso Coletivo / Férias da Fábrica</option>
                    <option value="municipal">Feriado Municipal</option>
                    <option value="estadual">Feriado Estadual</option>
                    <option value="nacional">Feriado Nacional</option>
                    <option value="personalizado">Personalizado / Parada de Fábrica</option>
                  </select>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => setIsManualModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-lg text-xs font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvandoManual}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {salvandoManual ? (
                    <span>Salvando...</span>
                  ) : (
                    <>
                      <Plus size={14} />
                      <span>Cadastrar Data</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
